import { Box, Button, Skeleton, Text, VStack } from "@chakra-ui/react";
import { Hint } from "@/ui/Hint";
import { useEffect, useRef, useState } from "react";
import { LuExternalLink, LuRefreshCw } from "react-icons/lu";
import { PageContainer } from "@/ui/containers/PageContainer";
import type { BreadcrumbItem } from "@/utils/routes";

type Props = {
  title: string;
  description?: string;
  breadcrumb?: BreadcrumbItem[];
  src?: string;
  html?: string;
  aspectRatio?: number;
  autoHeight?: boolean;
  sandbox?: string;
  openUrl?: string | null;
  openLabel?: string;
};

const LOOKER_SANDBOX =
  "allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-downloads";

const toLookerUrl = (src: string) =>
  src.replace("/embed/reporting/", "/reporting/");

const isLooker = (src?: string) =>
  !!src && src.includes("lookerstudio.google.com");

export function EmbeddedReport({
  title,
  description,
  breadcrumb,
  src,
  html,
  aspectRatio = 4 / 3,
  autoHeight = false,
  sandbox = LOOKER_SANDBOX,
  openUrl,
  openLabel,
}: Props) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!autoHeight) return;
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow) return;
      if (event.data?.tipo !== "geslub:alto") return;
      const height = Number(event.data.alto);
      if (Number.isFinite(height) && height > 0) setMeasuredHeight(height);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [autoHeight]);

  useEffect(() => {
    setLoaded(false);
    setMeasuredHeight(null);
  }, [src, html, reloadKey]);

  const resolvedOpenUrl =
    openUrl === undefined
      ? isLooker(src)
        ? toLookerUrl(src as string)
        : src ?? null
      : openUrl;
  const resolvedOpenLabel =
    openLabel ??
    (isLooker(src) ? "Abrir en Looker Studio" : "Abrir en nueva pestaña");

  return (
    <PageContainer
      title={title}
      description={description}
      breadcrumb={breadcrumb}
      actions={
        <>
          <Button
            variant="outline"
            onClick={() => setReloadKey((key) => key + 1)}
          >
            <LuRefreshCw />
            Recargar
          </Button>
          {resolvedOpenUrl && (
            <Button variant="outline" asChild>
              <a
                href={resolvedOpenUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LuExternalLink />
                {resolvedOpenLabel}
              </a>
            </Button>
          )}
        </>
      }
    >
      {isLooker(src) && (
        <Hint mb={4}>
          Este reporte se muestra desde Looker Studio y requiere una sesión de
          Google con acceso. Si ves «No se puede acceder al informe», inicia
          sesión en Google en este navegador o ábrelo con el botón de arriba.
        </Hint>
      )}
      <Box
        position="relative"
        w="full"
        aspectRatio={measuredHeight ? undefined : aspectRatio}
        h={measuredHeight ? `${measuredHeight}px` : undefined}
        minH="640px"
        bg="bg.panel"
        borderWidth="1px"
        borderColor="border"
        rounded="xl"
        shadow="xs"
        overflow="hidden"
      >
        {!loaded && (
          <VStack
            position="absolute"
            inset={0}
            p={6}
            gap={4}
            align="stretch"
            bg="bg.panel"
            zIndex={1}
            aria-live="polite"
          >
            <Skeleton h="12" rounded="md" />
            <Skeleton h="8" w="40%" rounded="md" />
            <Skeleton flex={1} rounded="md" />
            <Text fontSize="sm" color="fg.muted" textAlign="center">
              Cargando reporte…
            </Text>
          </VStack>
        )}
        <iframe
          key={reloadKey}
          ref={frame}
          {...(html !== undefined ? { srcDoc: html } : { src })}
          title={title}
          allowFullScreen
          onLoad={() => setLoaded(true)}
          sandbox={sandbox}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: "none",
          }}
        />
      </Box>
    </PageContainer>
  );
}
